import React, { useEffect, useRef } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { parseUpdatePlanProgress } from '../../utils/planProgress';

/*
 * Plan progress flow (high level):
 * 1. presentPlan (PlanContent) renders first in the message list and calls setPresentPlan(conversationId, messageId).
 *    That sets lastPresentPlanMessageId so the store knows which plan later updatePlanProgress blocks apply to.
 * 2. updatePlanProgress tool blocks (this component) run in order. Each parses completedStepIndices /
 *    inProgressStepIndices / skippedStepIndices / note and calls applyPlanProgress(conversationId, update). Progress is stored
 *    under planProgressByPlanMessageId[lastPresentPlanMessageId].
 * 3. PlanContent reads that progress and shows checkmarks / in-progress spinners. This component renders
 *    nothing so the user doesn't see a duplicate "progress updated" block.
 *
 * Re-visiting a chat: When the user switches away and back, the conversation may be refreshed from the DB
 * (which doesn't persist plan progress). We detect "appliedKeys has this block but store has no progress for
 * this plan" and clear appliedKeys for that conversation so every updatePlanProgress block re-applies in order,
 * restoring the full completed state.
 */

/** Unique key for "we have applied this block" — conversationId + blockId. Used to avoid double-apply and to decide when to re-apply on revisit. */
function toAppliedKey(conversationId: string, blockId: string): string {
  return `${conversationId}\n${blockId}`;
}

/**
 * Maximum number of appliedKey entries to keep in memory.
 * This bounds the growth of the in-memory tracking structure to avoid leaks
 * in long-running sessions while still preventing double-application for
 * the most recent blocks.
 */
const MAX_APPLIED_KEYS = 1000;

/**
 * BoundedSet wraps a Set and limits the number of stored items.
 * It supports the subset of the Set API used in this file: has, add, delete, forEach.
 */
class BoundedSet<T> {
  private readonly set = new Set<T>();
  private readonly queue: T[] = [];

  constructor(private readonly maxSize: number) {}

  has(value: T): boolean {
    return this.set.has(value);
  }

  add(value: T): this {
    if (this.set.has(value)) {
      return this;
    }

    this.set.add(value);
    this.queue.push(value);

    if (this.queue.length > this.maxSize) {
      const oldest = this.queue.shift();
      if (oldest !== undefined) {
        this.set.delete(oldest);
      }
    }

    return this;
  }

  delete(value: T): boolean {
    const deleted = this.set.delete(value);
    if (deleted) {
      const index = this.queue.indexOf(value);
      if (index !== -1) {
        this.queue.splice(index, 1);
      }
    }
    return deleted;
  }

  forEach(callback: (value: T) => void): void {
    this.set.forEach((value) => {
      callback(value);
    });
  }
}

/**
 * Bounded set of appliedKey values. Module-level so we don't re-apply
 * the same block twice in the recent session history, but bounded to
 * avoid unbounded memory growth.
 */
const appliedKeys = new BoundedSet<string>(MAX_APPLIED_KEYS);
/** Removes all applied keys for a conversation so every updatePlanProgress block can re-apply (used when re-visiting a chat that lost progress). */
function clearAppliedKeysForConversation(conversationId: string): void {
  const prefix = `${conversationId}\n`;
  appliedKeys.forEach((key) => {
    if (key.startsWith(prefix)) {
      appliedKeys.delete(key);
    }
  });
}

interface UpdatePlanProgressContentProps {
  content: ToolUseBlock;
}

/**
 * Invisible handler for updatePlanProgress tool blocks.
 *
 * The tool returns completedStepIndices, inProgressStepIndices, skippedStepIndices, and optional note (no message).
 * We parse the block input and apply it to the store so PlanContent can show checkmarks, in-progress spinners, and skipped steps. We render null so the chat does not show a separate "progress updated" message.
 *
 * Order dependency: applyPlanProgress uses lastPresentPlanMessageId, which is set by PlanContent (presentPlan).
 * So we wait for that (or retry after 100ms) before applying.
 */
export const UpdatePlanProgressContent: React.FC<
  UpdatePlanProgressContentProps
> = ({ content }) => {
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const lastPresentPlanMessageId = useChatStore((state) =>
    activeConversationId
      ? state.conversations[activeConversationId]?.lastPresentPlanMessageId
      : undefined,
  );
  const hasAppliedRef = useRef(false);

  const blockId = content.id ?? '';
  const raw =
    typeof content.input === 'object' && content.input !== null
      ? content.input
      : null;
  const parsedRef = useRef(parseUpdatePlanProgress(raw));
  parsedRef.current = parseUpdatePlanProgress(raw);
  const hasUpdate =
    (parsedRef.current.completedStepIndices?.length ?? 0) > 0 ||
    (parsedRef.current.inProgressStepIndices?.length ?? 0) > 0 ||
    (parsedRef.current.skippedStepIndices?.length ?? 0) > 0;

  useEffect(() => {
    if (!activeConversationId || !hasUpdate || hasAppliedRef.current) {
      return;
    }

    const conv = useChatStore.getState().conversations[activeConversationId];
    const planMessageId = conv?.lastPresentPlanMessageId;
    // applyPlanProgress no-ops without a plan; wait for PlanContent to run setPresentPlan
    if (planMessageId === undefined) {
      // When PlanContent calls setPresentPlan, lastPresentPlanMessageId will change
      // and this effect will re-run due to its dependency array.
      return;
    }

    const appliedKey = toAppliedKey(activeConversationId, blockId);
    if (appliedKeys.has(appliedKey)) {
      // Only skip if *this* plan already has progress (not some other plan in the same conversation)
      const current = conv?.planProgressByPlanMessageId?.[planMessageId];
      const hasProgress =
        current &&
        ((current.completedStepIndices?.length ?? 0) > 0 ||
          (current.inProgressStepIndices?.length ?? 0) > 0 ||
          (current.skippedStepIndices?.length ?? 0) > 0);
      if (hasProgress) {
        return;
      }
      // Re-visit: store was overwritten (e.g. from DB). Clear keys so all blocks re-apply in order.
      clearAppliedKeysForConversation(activeConversationId);
    }

    const parsed = parsedRef.current;
    hasAppliedRef.current = true;
    appliedKeys.add(appliedKey);
    useChatStore.getState().applyPlanProgress(activeConversationId, {
      completedStepIndices: parsed.completedStepIndices,
      inProgressStepIndices: parsed.inProgressStepIndices,
      skippedStepIndices: parsed.skippedStepIndices,
      note: parsed.note,
    });
  }, [activeConversationId, blockId, lastPresentPlanMessageId, hasUpdate]);

  return null;
};
